import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      modifier — «UI nuova» field wiring (components/fields.md §4): on the
 *              wrapper of a field's control(s), it ties each control to its
 *              messages so a screen reader reads them, without touching the
 *              yielded control: `aria-invalid="true"` while the field is invalid
 *              and `aria-describedby` → the error / help ids.
 *              `<div {{nv-field-a11y invalid=bool describedBy='id1 id2' for=id}}>`
 *   behaviour: controls = the element whose id is `for` (inside the wrapper,
 *              else in the document), otherwise every form control inside the
 *              wrapper (input / select / textarea / power-select trigger /
 *              role combobox|textbox|checkbox|radio|switch|listbox /
 *              contenteditable) that is not inside a nested wrapper. A control
 *              is invalid when `invalid` is true OR it (or a wrapper of it
 *              inside the field) carries the `.nv-invalid` class (the 174
 *              hand-written invalid frames get the attribute for free).
 *              Only the tokens it added are removed again: a describedby /
 *              aria-invalid the template wrote itself is kept. Bookkeeping is
 *              per instance (describedby tokens in a Map on the instance,
 *              aria-invalid owners in a shared WeakMap), never in DOM marks,
 *              so nested / `for`-crossing wrappers do not take each other's
 *              tokens; the destructor gives every token back. A
 *              MutationObserver (child list + class + data-nv-field-scope)
 *              re-applies when the control renders later ({{#if}},
 *              power-select re-render), its invalid class flips, or a nested
 *              wrapper installs; attributes are written only on change.
 *   args:      named `invalid` (boolean), `describedBy` (space-separated ids,
 *              null when none), `for` (id of THE control, optional)
 *   uses:      ember-modifier (class)
 *   users:     NvFormField (beebee-base-template), NvSettingsField (help)
 *   gotchas:   the ids must exist in the DOM when invalid (NvFormField renders
 *              the error wrapper only while there is a message, and passes its
 *              id only then). Classic UI too: the attributes are plain a11y,
 *              no look depends on them. A template inside a wired field must
 *              not BIND aria-invalid itself: a template value that turns
 *              "true" while the modifier owns the attribute is removed with
 *              the modifier's own, and a template "false" is overridden while
 *              the field is invalid (no site does it today).
 */
const CONTROLS = ['input:not([type="hidden"], [type="button"], [type="submit"], [type="reset"], [type="image"])', 'select', 'textarea', '.ember-power-select-trigger', '[role="combobox"]', '[role="textbox"]', '[role="checkbox"]', '[role="radio"]', '[role="switch"]', '[role="listbox"]', '[contenteditable="true"]'].join(', ');
const SCOPE = 'data-nv-field-scope';

// aria-invalid owners, shared by every instance: control -> Set<modifier>. The attribute stays while ANY wrapper
// holds the control invalid and goes with the last one, so a nested / `for`-crossing wrapper never removes the
// attribute of another one. An aria-invalid="true" found with no owner is the template's: never claimed, never removed.
const INVALID_OWNERS = new WeakMap();
const tokens = value => value ? String(value).split(/\s+/).filter(Boolean) : [];
class NvFieldA11yModifier extends Modifier {
  constructor(owner, args) {
    super(owner, args);
    this.element = null;
    this.observer = null;
    this.state = {
      invalid: false,
      describedBy: [],
      forId: null
    };
    // control -> the aria-describedby tokens THIS instance added (per instance: a nested wrapper's tokens are foreign)
    this.added = new Map();
    registerDestructor(this, () => {
      // give back what was added: a control outside the wrapper (`for` document fallback) outlives it
      for (const control of [...this.added.keys()]) this.clean(control);
      this.observer?.disconnect();
    });
  }
  modify(element, positional, named) {
    if (this.element !== element) {
      this.observer?.disconnect();
      this.element = element;
      element.setAttribute(SCOPE, '');
      if (typeof MutationObserver === 'function') {
        this.observer = new MutationObserver(() => this.apply());
        // SCOPE too: a nested wrapper installs AFTER its parent (parent-first modifier install) and only then marks
        // itself — the parent must re-scan and give its controls back
        this.observer.observe(element, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', SCOPE]
        });
      }
    }
    this.state = {
      invalid: Boolean(named.invalid),
      describedBy: tokens(named.describedBy),
      forId: named.for ? String(named.for) : null
    };
    this.apply();
  }
  controls() {
    const el = this.element;
    if (!el) return [];
    const {
      forId
    } = this.state;
    if (forId) {
      const byId = [...el.querySelectorAll('[id]')].find(n => n.id === forId) || el.ownerDocument.getElementById(forId);
      if (byId) return [byId];
    }
    return [...el.querySelectorAll(CONTROLS)].filter(c => c.closest(`[${SCOPE}]`) === el);
  }
  isInvalid(control) {
    if (this.state.invalid) return true;
    const marked = control.closest('.nv-invalid');
    return Boolean(marked && this.element.contains(marked));
  }
  claimInvalid(control) {
    const owners = INVALID_OWNERS.get(control);
    if (owners?.has(this)) return;
    if (!owners && control.getAttribute('aria-invalid') === 'true') return; // the template's own: leave it alone
    if (owners) owners.add(this);else INVALID_OWNERS.set(control, new Set([this]));
    if (control.getAttribute('aria-invalid') !== 'true') control.setAttribute('aria-invalid', 'true');
  }
  releaseInvalid(control) {
    const owners = INVALID_OWNERS.get(control);
    if (!owners?.delete(this)) return;
    if (owners.size) return;
    INVALID_OWNERS.delete(control);
    control.removeAttribute('aria-invalid');
  }
  apply() {
    const el = this.element;
    if (!el) return;
    const current = this.controls();
    const wanted = this.state.describedBy;
    // controls that left the wrapper (or a `for` that changed): give back what was added
    for (const old of [...this.added.keys()]) {
      if (!current.includes(old)) this.clean(old);
    }
    for (const control of current) {
      if (this.isInvalid(control)) this.claimInvalid(control);else this.releaseInvalid(control);
      // aria-describedby: own tokens replaced, foreign tokens (template's, another wrapper's) kept
      const added = this.added.get(control) || [];
      const foreign = tokens(control.getAttribute('aria-describedby')).filter(t => !added.includes(t));
      const mine = wanted.filter(t => !foreign.includes(t));
      const value = [...foreign, ...mine].join(' ');
      // write only on change: apply() runs on every class flip in the subtree (Quill toolbar inside a field)
      if (value) {
        if (control.getAttribute('aria-describedby') !== value) control.setAttribute('aria-describedby', value);
      } else if (control.hasAttribute('aria-describedby')) {
        control.removeAttribute('aria-describedby');
      }
      this.added.set(control, mine);
    }
  }
  clean(control) {
    this.releaseInvalid(control);
    const added = this.added.get(control) || [];
    this.added.delete(control);
    if (!added.length) return;
    const rest = tokens(control.getAttribute('aria-describedby')).filter(t => !added.includes(t));
    if (rest.length) {
      const value = rest.join(' ');
      if (control.getAttribute('aria-describedby') !== value) control.setAttribute('aria-describedby', value);
    } else if (control.hasAttribute('aria-describedby')) {
      control.removeAttribute('aria-describedby');
    }
  }
}

export { NvFieldA11yModifier as default };
//# sourceMappingURL=nv-field-a11y.js.map
